import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Link,
  Spinner,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { FaArrowDown, FaArrowUp, FaFlag } from "react-icons/fa";
import {
  type JuryRankedEntryDto,
  type ReceivedBidDto,
  SourcingEventStatus,
  type SourcingEventDto,
  useConfirmJuryReportMutation,
  useFlagIdentifyingEntryMutation,
  useListBidsForEventQuery,
  useWriteJuryReportMutation,
  useRemoveJuryLinkMutation,
  useListStrandedJuryMembersQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { ListLoadError } from "../../../common/ListLoadError";
import { hasEarlyBidAccess } from "../bidAccess";
import { PublishedEvaluationNotice } from "../evaluation/EvaluationTab";
import { urls } from "../../../urls";
import { useCanManageSourcingEvent } from "../useCanManageSourcingEvent";
import {
  entriesWithoutAssessment,
  flaggedAmong,
  type JuryDraft,
  juryProgress,
  rankedDraft,
  readJuryDraft,
  writeJuryDraft,
} from "./jury";

export const JuryTab = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const apiError = useApiError();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const confirmationModal = useConfirmationModal();
  const mayManage = useCanManageSourcingEvent(event);
  const contest = event.eventContext.type === "Public" ? event.eventContext.designContest : null;
  const { data, isLoading, isError, refetch } = useListBidsForEventQuery({ eventId: event.id });
  const [writeReport, { isLoading: isSaving }] = useWriteJuryReportMutation();
  const [confirm] = useConfirmJuryReportMutation();
  const [confirmingIndex, setConfirmingIndex] = useState<number | null>(null);
  const [flag, { isLoading: isFlagging }] = useFlagIdentifyingEntryMutation();
  const [removeLink] = useRemoveJuryLinkMutation();
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const hasLinkedMember = (contest?.jury ?? []).some((member) => member.personId != null);
  const {
    data: strandedData,
    isError: strandedCheckFailed,
    refetch: recheckStranded,
  } = useListStrandedJuryMembersQuery(
    { eventId: event.id },
    { skip: !mayManage || !hasLinkedMember || event.status !== SourcingEventStatus.Evaluation },
  );
  const stranded = new Set(strandedData?.memberIndexes ?? []);
  const entries = useMemo(() => (data?.bids ?? []).filter((bid) => bid.status !== "DRAFT"), [data]);
  const savedReport = contest?.juryReport ?? null;
  const [ranking, setRanking] = useState<JuryRankedEntryDto[]>([]);
  const [remarks, setRemarks] = useState("");
  const [questions, setQuestions] = useState("");
  const [flagging, setFlagging] = useState<string | null>(null);
  const [flagReason, setFlagReason] = useState("");

  const seedKey = `${savedReport?.writtenAt ?? ""}|${entries.map((entry) => entry.id).join(",")}`;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seededFor !== seedKey) {
    setSeededFor(seedKey);
    const entryIds = entries.map((entry) => entry.id);
    const stored = readJuryDraft(event.id, savedReport?.writtenAt);
    setRanking(rankedDraft(stored?.ranking ?? savedReport?.rankedEntries ?? [], entryIds));
    setRemarks(stored?.remarks ?? savedReport?.remarks ?? "");
    setQuestions(stored?.questions ?? savedReport?.questionsToParticipants ?? "");
  }

  const savedRanking = rankedDraft(savedReport?.rankedEntries ?? [], entries.map((entry) => entry.id));
  const hasUnsavedChanges =
    !!savedReport &&
    (JSON.stringify(ranking) !== JSON.stringify(savedRanking) ||
      remarks !== (savedReport.remarks ?? "") ||
      questions !== (savedReport.questionsToParticipants ?? ""));
  const discardChanges = () => {
    setRanking(savedRanking);
    setRemarks(savedReport?.remarks ?? "");
    setQuestions(savedReport?.questionsToParticipants ?? "");
    writeJuryDraft(event.id, savedReport?.writtenAt, null);
  };

  const keepDraft = (draft: JuryDraft) => {
    const unchanged =
      JSON.stringify(draft.ranking) === JSON.stringify(savedReport?.rankedEntries ?? []) &&
      draft.remarks === (savedReport?.remarks ?? "") &&
      draft.questions === (savedReport?.questionsToParticipants ?? "");
    writeJuryDraft(event.id, savedReport?.writtenAt, unchanged ? null : draft);
  };
  const updateRanking = (change: (current: JuryRankedEntryDto[]) => JuryRankedEntryDto[]) => {
    const next = change(ranking);
    setRanking(next);
    keepDraft({ ranking: next, remarks, questions });
  };
  const updateRemarks = (value: string) => {
    setRemarks(value);
    keepDraft({ ranking, remarks: value, questions });
  };
  const updateQuestions = (value: string) => {
    setQuestions(value);
    keepDraft({ ranking, remarks, questions: value });
  };

  if (!contest) return null;
  if (event.status === SourcingEventStatus.Published) {
    return <PublishedEvaluationNotice event={event} earlyAccess={hasEarlyBidAccess(event, selectedOrg)} />;
  }
  const decided = !event.entriesAnonymous;
  const juryWorkOpen = event.status === SourcingEventStatus.Evaluation && !decided;
  const editable = mayManage && juryWorkOpen;
  const isJuror = (contest.jury ?? []).some((member) => member.personId != null && member.personId === me.id);
  const mayFlag = juryWorkOpen && (mayManage || isJuror);
  const confirmedCount = (contest.juryConfirmations ?? []).length;
  const progress = juryProgress(contest);
  const labelOf = (bidId: string): string => entries.find((entry) => entry.id === bidId)?.ownerName ?? t("Entry") ?? "";
  const unassessed = entriesWithoutAssessment(savedReport);

  const move = (index: number, by: number) =>
    updateRanking((current) => {
      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(index + by, 0, moved);
      return next;
    });

  const requestSave = () => {
    const flaggedNames = flaggedAmong(contest, ranking.map((ranked) => ranked.bidId)).map(labelOf);
    const reconfirms = !!savedReport && confirmedCount > 0;
    if (flaggedNames.length === 0 && !reconfirms) {
      void save();
      return;
    }
    confirmationModal.show({
      title: flaggedNames.length > 0 ? t("Rank entries the jury has flagged?") : t("Save the changes to the report?"),
      description: [
        flaggedNames.length > 0
          ? t("The jury has flagged {{entries}} as identifying. Ranking it is your judgement to make.", {
              entries: flaggedNames.join(", "),
              count: flaggedNames.length,
            })
          : null,
        reconfirms
          ? t(
              "{{count}} jury members have confirmed this report. Saving the changes asks all of them to confirm again.",
              { count: confirmedCount },
            )
          : null,
      ]
        .filter(Boolean)
        .join(" "),
      confirmButtonText: String(t("Save the report")),
      onConfirmed: save,
    });
  };

  const save = async () => {
    try {
      await writeReport({
        eventId: event.id,
        writeJuryReportRequest: { rankedEntries: ranking, remarks, questionsToParticipants: questions },
      }).unwrap();
      writeJuryDraft(event.id, savedReport?.writtenAt, null);
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const requestLinkRemoval = (memberIndex: number, name: string) =>
    confirmationModal.show({
      title: t("Remove {{name}}'s link to their user?", { name }),
      description: t(
        "{{name}} can no longer open the procurement. Without the link you record their confirmation, given outside SourceMagnet. The protocol shows that you removed the link. This can't be undone.",
        { name },
      ),
      confirmButtonText: String(t("Remove the link")),
      onConfirmed: async () => {
        setRemovingIndex(memberIndex);
        try {
          await removeLink({ eventId: event.id, removeJuryLinkRequest: { memberIndex } }).unwrap();
        } catch (e) {
          apiError.trigger(e);
        } finally {
          setRemovingIndex(null);
        }
      },
    });

  const requestConfirmation = (memberIndex: number, name: string, isMe: boolean) => {
    const shownVersion = savedReport?.writtenAt;
    if (!shownVersion) return;
    confirmationModal.show({
      title: isMe
        ? t("Confirm the jury's report as {{name}}?", { name })
        : t("Record that {{name}} has confirmed the jury's report outside SourceMagnet? This can't be undone.", {
            name,
          }),
      description: t("A confirmation can't be withdrawn. Changing the report later asks every member to confirm again."),
      onConfirmed: () => confirmFor(memberIndex, shownVersion),
    });
  };

  const confirmFor = async (memberIndex: number, reportWrittenAt: string) => {
    setConfirmingIndex(memberIndex);
    try {
      await confirm({ eventId: event.id, confirmJuryReportRequest: { memberIndex, reportWrittenAt } }).unwrap();
    } catch (e) {
      apiError.trigger(e);
    } finally {
      setConfirmingIndex(null);
    }
  };

  const submitFlag = async (bidId: string) => {
    try {
      await flag({ eventId: event.id, flagEntryRequest: { bidId, reason: flagReason } }).unwrap();
      setFlagging(null);
      setFlagReason("");
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Stack spacing="6">
      {confirmationModal.modal}
      {event.entriesAnonymous && (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          {t(
            "The entries are anonymous until the jury's decision is registered. Nobody sees who is behind an entry until then.",
          )}
        </Alert>
      )}

      <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
        <Heading as="h3" size="sm">
          {t("The jury's ranking")}
        </Heading>
        {editable && (
          <Text fontSize="sm" color="smMuted" mt="1">
            {t("Rank the entries by the criteria in the notice and give the jury's assessment of each.")}
          </Text>
        )}
        {(contest.juryCriteria ?? []).length > 0 && (
          <Text fontSize="xs" color="smMuted" mt="2">
            {t("Criteria: {{criteria}}", { criteria: (contest.juryCriteria ?? []).join(" · ") })}
          </Text>
        )}
        {isLoading ? (
          <Spinner size="sm" mt="4" />
        ) : isError ? (
          <Box mt="4">
            <ListLoadError message={t("Could not load the entries.")} onRetry={refetch} />
          </Box>
        ) : entries.length === 0 ? (
          <Text fontSize="sm" color="smMuted" mt="4">
            {t("No entries have been delivered.")}
          </Text>
        ) : (
          <Stack spacing="3" mt="4">
            {ranking.map((ranked, index) => (
              <EntryRow
                key={ranked.bidId}
                place={index + 1}
                label={labelOf(ranked.bidId)}
                entryHref={urls.app.sourcingEvents.bidDetail.go(event.id, ranked.bidId)}
                entry={entries.find((entry) => entry.id === ranked.bidId)}
                assessment={ranked.assessment}
                editable={editable}
                flagged={(contest.entryFlags ?? []).filter((f) => f.bidId === ranked.bidId).map((f) => f.reason)}
                onAssessment={(assessment) =>
                  updateRanking((current) =>
                    current.map((item) => (item.bidId === ranked.bidId ? { ...item, assessment } : item)),
                  )
                }
                onUp={index > 0 ? () => move(index, -1) : undefined}
                onDown={index < ranking.length - 1 ? () => move(index, 1) : undefined}
                onFlag={mayFlag ? () => setFlagging(ranked.bidId) : undefined}
              />
            ))}
          </Stack>
        )}
        {flagging && (
          <Box mt="4" borderWidth="1px" borderColor="smBorder" borderRadius="md" p="3">
            <FormControl>
              <FormLabel fontSize="sm">
                {t("Why does {{entry}} give its participant away?", { entry: labelOf(flagging) })}
              </FormLabel>
              <Input size="sm" value={flagReason} onChange={(e) => setFlagReason(e.target.value)} />
            </FormControl>
            <HStack mt="2">
              <Button
                size="sm"
                colorScheme="teal"
                isDisabled={!flagReason.trim()}
                isLoading={isFlagging}
                onClick={() => submitFlag(flagging)}
              >
                {t("Flag the entry")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setFlagging(null)}>
                {t("Cancel")}
              </Button>
            </HStack>
          </Box>
        )}
        {editable ? (
          <>
            <FormControl mt="5">
              <FormLabel fontSize="sm">{t("The jury's remarks")}</FormLabel>
              <Textarea size="sm" value={remarks} onChange={(e) => updateRemarks(e.target.value)} />
            </FormControl>
            <FormControl mt="4">
              <FormLabel fontSize="sm">{t("Questions for the participants")}</FormLabel>
              <Textarea size="sm" value={questions} onChange={(e) => updateQuestions(e.target.value)} />
            </FormControl>
          </>
        ) : (
          <>
            <ReportText mt="5" heading={String(t("The jury's remarks"))} text={remarks} />
            <ReportText mt="4" heading={String(t("Questions for the participants"))} text={questions} />
          </>
        )}
        {editable && entries.length > 0 && (
          <HStack mt="4">
            <Button size="sm" colorScheme="teal" onClick={requestSave} isLoading={isSaving}>
              {savedReport ? t("Save the report") : t("Write the jury's report")}
            </Button>
            {hasUnsavedChanges && (
              <Button size="sm" variant="ghost" onClick={discardChanges} isDisabled={isSaving}>
                {t("Discard the changes")}
              </Button>
            )}
          </HStack>
        )}
        {savedReport && (
          <Text fontSize="xs" color="smMuted" mt="2">
            {t("Saved {{date}}. Saving it again asks every member to confirm once more.", {
              date: displayDate(savedReport.writtenAt),
            })}
          </Text>
        )}
      </Box>

      <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
        <Heading as="h3" size="sm">
          {t("Confirmations")}
        </Heading>
        <Text fontSize="sm" color="smMuted" mt="1">
          {t("{{done}} of {{total}} jury members have confirmed the report", progress)}
        </Text>
        {!savedReport && (
          <Text fontSize="sm" mt="3">
            {t("The jury can confirm once the report is written.")}
          </Text>
        )}
        {unassessed.length > 0 && (
          <Text fontSize="sm" color="smWarning" mt="3">
            {t("The jury can confirm once every ranked entry has its assessment. Missing for: {{entries}}.", {
              entries: unassessed.map(labelOf).join(", "),
            })}
          </Text>
        )}
        {hasUnsavedChanges && juryWorkOpen && (
          <Text fontSize="sm" color="smWarning" mt="3">
            {t("The report has unsaved changes. Save or discard them before anyone confirms it.")}
          </Text>
        )}
        {strandedCheckFailed && (
          <Box mt="3">
            <ListLoadError
              message={t("We couldn't check whether every linked jury member can still open the procurement.")}
              onRetry={recheckStranded}
            />
          </Box>
        )}
        <Stack spacing="2" mt="3">
          {(contest.jury ?? []).map((member, index) => {
            const confirmation = (contest.juryConfirmations ?? []).find((c) => c.memberIndex === index);
            const isMe = member.personId != null && member.personId === me.id;
            const canRecord =
              juryWorkOpen &&
              !!savedReport &&
              unassessed.length === 0 &&
              !hasUnsavedChanges &&
              !confirmation &&
              (isMe || (member.personId == null && mayManage));
            return (
              <HStack key={`${member.name}-${index}`} justify="space-between" flexWrap="wrap">
                <Box>
                  <Text fontSize="sm">{[member.name, member.role].filter(Boolean).join(", ")}</Text>
                  {stranded.has(index) && !confirmation && (
                    <Text fontSize="xs" color="smWarning">
                      {t("Can no longer open the procurement, so can't confirm the report.")}
                    </Text>
                  )}
                </Box>
                {stranded.has(index) && !confirmation ? (
                  <Button
                    size="xs"
                    variant="outline"
                    isLoading={removingIndex === index}
                    isDisabled={removingIndex != null && removingIndex !== index}
                    onClick={() => requestLinkRemoval(index, member.name ?? "")}
                  >
                    {t("Remove the link")}
                  </Button>
                ) : confirmation ? (
                  <Badge colorScheme="green">
                    {confirmation.recordedOnBehalf
                      ? t("Confirmed outside SourceMagnet {{date}}", { date: displayDate(confirmation.confirmedAt) })
                      : t("Confirmed {{date}}", { date: displayDate(confirmation.confirmedAt) })}
                  </Badge>
                ) : canRecord ? (
                  <Button
                    size="xs"
                    variant="outline"
                    isLoading={confirmingIndex === index}
                    isDisabled={confirmingIndex != null && confirmingIndex !== index}
                    onClick={() => requestConfirmation(index, member.name ?? "", isMe)}
                  >
                    {isMe ? t("Confirm the report") : t("Record their confirmation")}
                  </Button>
                ) : savedReport ? (
                  <Badge>{t("Not confirmed")}</Badge>
                ) : null}
              </HStack>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

const EntryRow = ({
  place,
  label,
  entryHref,
  entry,
  assessment,
  editable,
  flagged,
  onAssessment,
  onUp,
  onDown,
  onFlag,
}: {
  place: number;
  label: string;
  entryHref: string;
  entry?: ReceivedBidDto;
  assessment: string;
  editable: boolean;
  flagged: string[];
  onAssessment: (value: string) => void;
  onUp?: () => void;
  onDown?: () => void;
  onFlag?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p="3">
      <HStack justify="space-between">
        <Text fontSize="sm" fontWeight="semibold">
          {`${place}. `}
          <Link as={RouterLink} to={entryHref} color="smPrimary">
            {label}
          </Link>{" "}
          {entry && (
            <Text as="span" fontSize="xs" color="smMuted" fontWeight="normal">
              {t("{{count}} documents", { count: entry.documents?.length ?? 0 })}
            </Text>
          )}
        </Text>
        <HStack spacing="1">
          {editable && (
            <>
              <IconButton size="xs" variant="ghost" aria-label={t("Move up") ?? undefined} icon={<FaArrowUp />} isDisabled={!onUp} onClick={onUp} />
              <IconButton size="xs" variant="ghost" aria-label={t("Move down") ?? undefined} icon={<FaArrowDown />} isDisabled={!onDown} onClick={onDown} />
            </>
          )}
          {onFlag && (
            <IconButton size="xs" variant="ghost" aria-label={t("Flag as identifying") ?? undefined} icon={<FaFlag />} onClick={onFlag} />
          )}
        </HStack>
      </HStack>
      {flagged.map((reason, index) => (
        <Text key={`${index}-${reason}`} fontSize="xs" color="smError" mt="1">
          {t("Flagged: {{reason}}", { reason })}
        </Text>
      ))}
      {editable ? (
        <Textarea
          mt="2"
          size="sm"
          value={assessment}
          placeholder={t("The jury's assessment of this entry") ?? undefined}
          onChange={(e) => onAssessment(e.target.value)}
        />
      ) : (
        <ReportText mt="2" text={assessment} />
      )}
      {!assessment.trim() && (
        <Text fontSize="xs" color="smWarning" mt="1">
          {t("The jury's assessment is missing.")}
        </Text>
      )}
    </Box>
  );
};

const ReportText = ({ heading, text, mt }: { heading?: string; text: string; mt: string }) => {
  const { t } = useTranslation();
  const content = text.trim();
  return (
    <Box mt={mt}>
      {heading && (
        <Text fontSize="sm" fontWeight="semibold" color="smText">
          {heading}
        </Text>
      )}
      <Text fontSize="sm" color={content ? "smText" : "smMuted"} whiteSpace="pre-wrap" mt={heading ? "1" : undefined}>
        {content || t("None")}
      </Text>
    </Box>
  );
};
